import { Close, EditRounded, VisibilityRounded } from '@mui/icons-material'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { IApprovalRequest } from '../../definitions/Approval'
import Formatter from '../../helpers/Formatter'
import ApprovalManager from '../../managers/ApprovalManager'

interface ApprovalModalProps {
    open: boolean
    onClose: () => void
    approval: IApprovalRequest
    onEdit?: (approval: IApprovalRequest) => void
}

const ApprovalModal = ({ open, onClose, approval, onEdit }: ApprovalModalProps) => {
    const theme = useTheme()

    const handleEdit = () => {
        if (onEdit && approval) {
            onEdit(approval)
        }
    }

    const renderDialogContent = () => {
        if (!approval) return null

        const lastChange = approval.changes.length > 0 ? approval.changes[approval.changes.length - 1] : null

        switch (approval.status) {
            case 'draft':
                return (
                    <>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={2}>
                                {ApprovalManager.statusField(approval.status)}
                                {ApprovalManager.infoField('Name', approval.entityName)}
                                {ApprovalManager.infoField('Drafted By', lastChange.userName ?? 'N/A')}
                                {ApprovalManager.infoField('Drafted At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                            </Stack>
                        </DialogContent>

                        <DialogActions sx={{ pb: 2 }}>
                            <Button
                                startIcon={approval.entityType === 'settlement' ? <VisibilityRounded /> : <EditRounded />}
                                variant='contained'
                                onClick={handleEdit}
                                sx={{ textTransform: 'none' }}>
                                {approval.entityType === 'settlement' ? 'View' : 'Edit'}
                            </Button>
                        </DialogActions>
                    </>
                )

            case 'ready-for-review':
                return (
                    <>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={2}>
                                {ApprovalManager.statusField(approval.status)}
                                {ApprovalManager.infoField('Name', approval.entityName)}
                                {ApprovalManager.infoField('Requested By', lastChange.userName ?? 'N/A')}
                                {ApprovalManager.infoField('Requested At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                            </Stack>
                        </DialogContent>
                    </>
                )

            case 'approved':
                return (
                    <>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={2}>
                                {ApprovalManager.statusField(approval.status)}
                                {ApprovalManager.infoField('Name', approval.entityName)}
                                {ApprovalManager.infoField('Approved By', lastChange.userName ?? 'N/A')}
                                {ApprovalManager.infoField('Approved At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                            </Stack>
                        </DialogContent>
                    </>
                )

            case 'rejected':
                return (
                    <>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack direction='row' spacing={4} mt={2}>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.statusField(approval.status)}
                                    {ApprovalManager.infoField('Name', approval.entityName)}
                                </Stack>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.infoField('Rejected By', lastChange.userName ?? 'N/A')}
                                    {ApprovalManager.infoField('Rejected At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                </Stack>
                            </Stack>
                        </DialogContent>

                        {lastChange?.description && (
                            <DialogContent sx={{ pb: 2 }}>
                                <Box>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Rejection Reason
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            p: 2,
                                            border: theme.palette.divider,
                                            borderRadius: '4px',
                                            backgroundColor: 'dimgray',
                                            minHeight: '200px',
                                            maxHeight: '250px',
                                            overflow: 'auto'
                                        }}>
                                        <Typography
                                            variant='body1'
                                            component='pre'
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                wordWrap: 'break-word',
                                                margin: 0,
                                                fontFamily: 'inherit',
                                                fontSize: '1rem',
                                                lineHeight: 1.4375
                                            }}>
                                            {lastChange?.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </DialogContent>
                        )}

                        <DialogActions sx={{ pb: 2 }}>
                            <Button
                                startIcon={<EditRounded />}
                                variant='contained'
                                onClick={handleEdit}
                                sx={{ textTransform: 'none' }}>
                                Edit
                            </Button>
                        </DialogActions>
                    </>
                )

            default:
                return null
        }
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth='sm'
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, minHeight: 300 } }}>
                <DialogTitle>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Typography variant='h6'>Approval Info</Typography>
                        <IconButton onClick={onClose} size='small'>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                {renderDialogContent()}
            </Dialog>
        </>
    )
}

export default ApprovalModal
